import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationSurface } from '@nexus/hub-contracts';

import {
  dismissNotification,
  fetchFeedNotifications,
  subscribeToNotificationSignals,
  type FeedNotification,
} from '../lib/notifications';

/** Polling covers a channel that is down, so an ended incident does not stay on screen. */
const POLL_WHILE_DISCONNECTED_MS = 30_000;
/** A signal can fail while the channels stay joined, because the trigger keeps a post when it cannot send. */
const POLL_WHILE_CONNECTED_MS = 5 * 60_000;
/** setTimeout runs a delay above this at once. */
const MAX_TIMER_MS = 2 ** 31 - 1;
const TIMER_SLACK_MS = 25;
const NONE: FeedNotification[] = [];
const NO_IDS: ReadonlySet<string> = new Set();

export interface UseNotificationFeedResult {
  /** Newest first. Leaves out what the user dismissed and what has ended. */
  notifications: FeedNotification[];
  /** Hides the notification at once and records the dismissal for every browser. */
  dismiss: (id: string) => void;
}

const endTime = (notification: FeedNotification) =>
  notification.expiresAt ? Date.parse(notification.expiresAt) : Number.POSITIVE_INFINITY;

/**
 * The banners for one surface (CLOUD-1289). The first read and every later read go through
 * PostgREST under row level security. Realtime only says when to read again.
 */
export function useNotificationFeed({
  surface,
  userId,
}: {
  surface: NotificationSurface;
  userId: string | undefined;
}): UseNotificationFeedResult {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['notifications', surface, userId] as const, [surface, userId]);
  const [connected, setConnected] = useState(false);
  const [dismissed, setDismissed] = useState<{ userId: string | undefined; ids: ReadonlySet<string> }>({
    userId,
    ids: NO_IDS,
  });
  const dismissedIds = dismissed.userId === userId ? dismissed.ids : NO_IDS;
  const [now, setNow] = useState(() => Date.now());

  const { data } = useQuery({
    queryKey,
    queryFn: () => fetchFeedNotifications(surface),
    enabled: Boolean(userId),
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchInterval: connected ? POLL_WHILE_CONNECTED_MS : POLL_WHILE_DISCONNECTED_MS,
  });
  const fetched = data ?? NONE;

  useEffect(() => {
    if (!userId) return undefined;
    setConnected(false);
    return subscribeToNotificationSignals({
      userId,
      onConnection: (isConnected) => {
        setConnected(isConnected);
        if (isConnected) void queryClient.invalidateQueries({ queryKey });
      },
      onSignal: (event, signal) => {
        if (!signal.surfaces.includes(surface)) return;
        if (event === 'expiry_changed') {
          queryClient.setQueryData<FeedNotification[]>(queryKey, (rows) =>
            rows?.map((row) => (row.id === signal.id ? { ...row, expiresAt: signal.expiresAt } : row))
          );
        }
        void queryClient.invalidateQueries({ queryKey });
      },
    });
  }, [userId, surface, queryClient, queryKey]);

  useEffect(() => {
    const next = Math.min(...fetched.map(endTime).filter((end) => end > now));
    if (next === Number.POSITIVE_INFINITY) return undefined;
    const wait = Math.min(next - Date.now(), MAX_TIMER_MS - TIMER_SLACK_MS);
    const timer = setTimeout(() => setNow(Date.now()), Math.max(wait, 0) + TIMER_SLACK_MS);
    return () => clearTimeout(timer);
  }, [fetched, now]);

  const notifications = useMemo(
    () => fetched.filter((row) => !dismissedIds.has(row.id) && endTime(row) > now),
    [fetched, dismissedIds, now]
  );

  const dismiss = useCallback(
    (id: string) => {
      if (dismissedIds.has(id)) return;
      setDismissed((current) => ({
        userId,
        ids: new Set(current.userId === userId ? current.ids : NO_IDS).add(id),
      }));
      dismissNotification(id).catch((err: unknown) =>
        console.warn('[@nexus/supabase] The dismissal was not saved. The notification can return on a reload.', err)
      );
    },
    [dismissedIds, userId]
  );

  return { notifications, dismiss };
}
